import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
} from "react";
import { StoreApi, createStore, useStore } from "zustand";

// components
import { toast, ToastContainer } from "react-toastify";

// types
import {
  IOrganizations,
  TOrganizationVerificationStatus,
} from "../../setup/types/Organisation";
import {
  deleteOrganizationAPI,
  getAllOrganizations,
  getOrganization,
  getOrganizationDocumentations,
  rejectOrganization,
  verifyOrganization,
} from "../../setup/api/services/organization";

interface OrganizationStore {
  loading: boolean;
  page: number;
  pageCount: number;
  organizations?: IOrganizations[];
  selectedOrganisation?: IOrganizations;
  verificationStatusFilter?:
    | TOrganizationVerificationStatus
    | TOrganizationVerificationStatus[];
  /** code → whether the organization uploaded any supporting document. Only
   * populated for PENDING organizations (the Requests tab), since that's the
   * only place the answer gates the status label and the accept/reject actions.
   * A missing entry means "not known" and is treated as no documents. */
  organizationsHaveDocuments: Record<string, boolean>;
  searchValue: string;
  setLoading: (value: boolean) => void;
  buttonLoading: boolean;
  setButtonLoading: (value: boolean) => void;
  setPage: (value: number) => void;
  setPageCount: (value: number) => void;
  setOrganizations: (organizations: IOrganizations[]) => void;
  setOrganizationsHaveDocuments: (value: Record<string, boolean>) => void;
  setSelectedOrganization: (organization?: IOrganizations) => void;
  setVerificationStatusFilter: (
    value?: TOrganizationVerificationStatus | TOrganizationVerificationStatus[]
  ) => void;
  searchOrganisation: (value: string) => void;
  deleteOrganisation: (value: string) => void;
  fetchOrganization: (code: string) => void;
  fetchOrganizations: () => void;
  verifyOrganisation: (code: string) => Promise<boolean>;
  rejectOrganisation: (code: string, reason: string) => Promise<void>;
}

const OrganisationContext = createContext<
  StoreApi<OrganizationStore> | undefined
>(undefined);

type OrganisationProviderProps = PropsWithChildren;

const OrganstionProvider = ({ children }: OrganisationProviderProps) => {
  const [store] = useState(() =>
    createStore<OrganizationStore>((set) => ({
      organizations: undefined,
      selectedOrganisation: undefined,
      loading: true,
      buttonLoading: false,
      page: 1,
      pageCount: 1,
      verificationStatusFilter: undefined,
      organizationsHaveDocuments: {},
      searchValue: "",
      setLoading: (value: boolean) => {
        set({ loading: value });
      },
      setPage: (page: number) => {
        set({ page });
      },
      setPageCount: (value: number) => {
        set({ pageCount: value });
      },
      setSelectedOrganization: (organization?: IOrganizations) => {
        set({ selectedOrganisation: organization ? organization : undefined });
      },
      setOrganizations: (organizations) => {
        set({ organizations: organizations });
      },
      setOrganizationsHaveDocuments: (value) => {
        set({ organizationsHaveDocuments: value });
      },
      setVerificationStatusFilter: (
        value?: TOrganizationVerificationStatus | TOrganizationVerificationStatus[]
      ) => {
        set({ verificationStatusFilter: value });
      },
      searchOrganisation: (searchValue) => {
        set({ searchValue });
        fetchOrganizationsList(
          searchValue,
          store.getState().verificationStatusFilter
        );
      },
      deleteOrganisation: (code: string) => {
        deleteOrganisation(code);
      },
      fetchOrganization: (code: string) => {
        fetchOrganization(code);
      },
      setButtonLoading: (value: boolean) => {
        set({ buttonLoading: value });
      },
      fetchOrganizations: () => {
        fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
      },
      verifyOrganisation: (code: string) => verifyOrganisation(code),
      rejectOrganisation: (code: string, reason: string) =>
        rejectOrganisation(code, reason),
    }))
  );

  const pageSize = 10;

  // The list endpoint doesn't say whether an organization uploaded its supporting
  // documents, so for PENDING rows we ask the attachments endpoint per organization.
  // Only PENDING rows need it: they're the only ones whose status label and
  // accept/reject actions depend on the answer (at most `pageSize` requests).
  const fetchPendingOrganizationsDocuments = useCallback(
    async (organizations: IOrganizations[]) => {
      const pendingCodes = (organizations || [])
        .filter((org) => org.verificationStatus === "PENDING" && org.code)
        .map((org) => org.code);

      if (pendingCodes.length === 0) {
        store.getState().setOrganizationsHaveDocuments({});
        return;
      }

      const results = await Promise.all(
        pendingCodes.map(async (code) => {
          try {
            const response = await getOrganizationDocumentations(code);
            return [code, (response.data || []).length > 0] as const;
          } catch (error) {
            // Treat an unreachable attachments endpoint as "no documents": the request
            // stays non-actionable rather than letting the admin approve blind.
            console.error(`Error fetching documents for ${code}:`, error);
            return [code, false] as const;
          }
        })
      );

      store
        .getState()
        .setOrganizationsHaveDocuments(Object.fromEntries(results));
    },
    [store]
  );

  const fetchOrganizationsList = useCallback(
    async (
      searchValue: string,
      verificationStatus?:
        | TOrganizationVerificationStatus
        | TOrganizationVerificationStatus[]
    ) => {
      store.getState().setLoading(true);
      try {
        const response = await getAllOrganizations(
          store?.getState().page - 1,
          pageSize,
          searchValue,
          verificationStatus
        );
        store.getState().setOrganizations(response.data.content);
        store.getState().setPageCount(response.data.totalPages);
        // Resolved inside the same loading window as the list so the Requests tab
        // paints the right status ("بانتظار رفع الملفات" vs. "قيد المراجعة") and the
        // right action state on first render, instead of flipping a moment later.
        await fetchPendingOrganizationsDocuments(response.data.content);
      } catch (error) {
        console.error;
      } finally {
        store.getState().setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store]
  );

  const verifyOrganisation = useCallback(
    async (code: string) => {
      store.getState().setButtonLoading(true);
      try {
        await verifyOrganization(code);
        await fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
        return true;
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
        return false;
      } finally {
        store.getState().setButtonLoading(false);
      }
    },
    [fetchOrganizationsList, store]
  );

  const rejectOrganisation = useCallback(
    async (code: string, reason: string) => {
      store.getState().setButtonLoading(true);
      try {
        await rejectOrganization(code, reason);
        await fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
      } finally {
        store.getState().setButtonLoading(false);
      }
    },
    [fetchOrganizationsList, store]
  );

  const refreshPage = useCallback(() => {
    window?.location?.reload();
  }, []);

  const deleteOrganisation = useCallback(
    async (code: string) => {
      store.getState().setButtonLoading(true);
      try {
        await deleteOrganizationAPI(code, { code: code });
        refreshPage();
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
        store.getState().setButtonLoading(false);
      }
    },
    [refreshPage, store]
  );

  const fetchOrganization = useCallback(
    async (code: string) => {
      store.getState().setLoading(true);
      try {
        const response = await getOrganization(code);
        store.getState().setSelectedOrganization(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        store.getState().setLoading(false);
      }
    },
    [store]
  );

  return (
    <OrganisationContext.Provider value={store}>
      {children}
      {/* ─── Toast Container ───────────────────────────── */}
      <ToastContainer />
    </OrganisationContext.Provider>
  );
};

export default OrganstionProvider;

export function useOrganisationStore<T>(
  selector: (state: OrganizationStore) => T
) {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error("Organisation Provider is missing");
  }

  return useStore(context, selector);
}
